import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LayoutChangeEvent, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import {
    DataProvider,
    LayoutProvider,
    RecyclerListView,
    RecyclerListViewProps,
} from "recyclerlistview";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import Loading from "../components/Loading";
import { useTheme } from "../hooks/use-theme";

// Initial row-height estimate for the LayoutProvider only. Real heights are measured after
// render (`forceNonDeterministicRendering`), so this affects just the first layout pass /
// initial scrollbar accuracy, never correctness — hence a single internal default suffices.
const ESTIMATED_ITEM_HEIGHT = 72;
const ROW_LAYOUT_TYPE = "row";
// Row prepended to the data when a scrolling header is provided. It carries the header node
// itself so a header whose CONTENT changes (a headband appearing, a screen switching mode) is
// seen as changed by the DataProvider and actually re-rendered — a constant sentinel always
// compared equal, which silently froze the header on its first paint.
const HEADER_ROW_MARKER = "__screenCardHeader";
type HeaderRow = { [HEADER_ROW_MARKER]: true; node: React.ReactNode };
const isHeaderRow = (row: unknown): row is HeaderRow =>
    typeof row === "object" && row !== null && HEADER_ROW_MARKER in row;

// Initial-reveal gate. In `forceNonDeterministicRendering` mode RLV positions every row at an
// offset derived from ESTIMATED_ITEM_HEIGHT, but renders it at its real (larger) height, so on
// the first frames rows overlap ("crushed" on top of each other) until an async, multi-frame
// relayout cascade rewrites their positions. We keep the list mounted (so it measures) but
// covered until that cascade goes quiet, then fade the cover out. `onItemLayout` is RLV's only
// per-row layout signal (no onReady/onContentSettled exists), so we detect quiescence by
// debouncing it.
//
// The gate hides the list behind an opaque cover rather than fading the LIST in, and that
// direction is load-bearing: the list carries no animated style at all, so it paints as soon as
// it mounts, and the cover is unmounted by a React state whatever the animation does. The
// reverse shape — animated opacity on the list, React state on the placeholder — meant two
// channels for one state, and on Android they diverged. Reanimated keeps an animated style
// frozen at its first-render value inside the React tree (`PropsFilter.filterNonAnimatedProps`
// caches it in `_initialPropsMap`), so the reveal existed only as a UI-thread mutation: it never
// landed, the placeholder went away on schedule, and the screen stayed blank for good
// (BAS-2861). The worst case here is a fade that snaps.
//
// The cover is opaque, which assumes a flat screen background — true of every virtualized
// screen today. A screen combining `backgroundGradient` with virtualization would need the
// cover to inherit that gradient instead.
//
// SETTLE_DEBOUNCE_MS must exceed one relayout round-trip on a slow device (the cascade can span
// ~0.5s on low-end Android); too short and we reveal mid-cascade. MAX_GATE_MS is a hard fallback
// so the list ALWAYS reveals (tiny/empty lists, a stalled cascade, or no layout event at all).
const SETTLE_DEBOUNCE_MS = 200;
const MAX_GATE_MS = 1200;
const REVEAL_FADE_MS = 150;

export type VirtualizedScreenBodyProps<T> = {
    items: ReadonlyArray<T>;
    renderItem: (item: T, index: number) => React.ReactElement;
    /**
     * Content rendered as the first, scrolling row of the list. `recyclerlistview` has no
     * ListHeaderComponent, so this lets a screen header scroll away with the content (like a
     * regular ScrollView) instead of being pinned. Its content may change while the list is
     * shown: the node travels in the header row's data, so a change re-renders that row only.
     */
    header?: React.ReactNode;
    /**
     * Equality predicate fed to RecyclerListView's DataProvider. Defaults to
     * reference inequality, which is correct when `items` are rebuilt immutably.
     */
    rowHasChanged?: (a: T, b: T) => boolean;
    refreshControl?: React.ReactElement;
    onScroll?: (offsetY: number, isAtBottom: boolean) => void;
    onEndReached?: () => void;
    onEndReachedThreshold?: number;
    /** Bottom spacer height, used to clear the absolute action bar / safe area. */
    footerHeight?: number;
    /** Extra footer content (e.g. a pagination loader) rendered above the spacer. */
    footerContent?: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

/**
 * Screen-level virtualized list body backed by `recyclerlistview`. It MUST be the
 * scroll container (never nested inside another ScrollView) and is given a bounded
 * height via its `flex: 1` wrapper. Item heights are heterogeneous, hence
 * `forceNonDeterministicRendering`.
 */
const VirtualizedScreenBody = <T,>({
    items,
    renderItem,
    header,
    rowHasChanged,
    refreshControl,
    onScroll,
    onEndReached,
    onEndReachedThreshold = 0.2,
    footerHeight = 0,
    footerContent,
    style,
}: VirtualizedScreenBodyProps<T>) => {
    const { colors } = useTheme();
    const [width, setWidth] = useState(0);
    const hasHeader = header != null;

    const onContainerLayout = useCallback((e: LayoutChangeEvent) => {
        setWidth(e.nativeEvent.layout.width);
    }, []);

    // Cover fade-out gate (see the *_MS constants above). `isRevealedRef` is a one-way latch
    // scoped to this component instance: once revealed we never re-cover on later
    // scroll / recycle / data change, and it re-arms only on a fresh mount. The timer callbacks
    // read the latch rather than state, so they are not re-created on every render.
    const isRevealedRef = useRef(false);
    const [isCoverMounted, setIsCoverMounted] = useState(true);
    const coverOpacity = useSharedValue(1);
    const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const maxTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const unmountTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const reveal = useCallback(() => {
        if (isRevealedRef.current) {
            return;
        }
        isRevealedRef.current = true;
        if (settleTimer.current) {
            clearTimeout(settleTimer.current);
        }
        if (maxTimer.current) {
            clearTimeout(maxTimer.current);
        }
        coverOpacity.value = withTiming(0, { duration: REVEAL_FADE_MS });
        // React drops the cover once the fade is over — and equally if the fade never ran, which
        // is what keeps a broken animation from hiding the list.
        unmountTimer.current = setTimeout(() => setIsCoverMounted(false), REVEAL_FADE_MS);
    }, [coverOpacity]);

    // Each measured row re-arms the quiescence timer; the cascade has converged once rows stop
    // re-measuring. The latch keeps later scroll/recycle layout events inert.
    const handleItemLayout = useCallback(() => {
        if (isRevealedRef.current) {
            return;
        }
        if (settleTimer.current) {
            clearTimeout(settleTimer.current);
        }
        settleTimer.current = setTimeout(reveal, SETTLE_DEBOUNCE_MS);
    }, [reveal]);

    // Arm the hard fallback once the list is actually mounted (width gate satisfied). This
    // effect's cleanup is the single teardown point, clearing every timer on unmount so nothing
    // fires afterwards.
    const isListMounted = width > 0;
    useEffect(() => {
        if (!isListMounted || isRevealedRef.current) {
            return;
        }
        maxTimer.current = setTimeout(reveal, MAX_GATE_MS);
        return () => {
            if (settleTimer.current) {
                clearTimeout(settleTimer.current);
            }
            if (maxTimer.current) {
                clearTimeout(maxTimer.current);
            }
            if (unmountTimer.current) {
                clearTimeout(unmountTimer.current);
            }
        };
    }, [isListMounted, reveal]);

    const coverStyle = useAnimatedStyle(() => ({ opacity: coverOpacity.value }));

    const dataProvider = useMemo(
        () =>
            new DataProvider((a: unknown, b: unknown) => {
                const aHeader = isHeaderRow(a);
                const bHeader = isHeaderRow(b);
                if (aHeader || bHeader) {
                    return aHeader !== bHeader || (aHeader && bHeader && a.node !== b.node);
                }
                return rowHasChanged ? rowHasChanged(a as T, b as T) : a !== b;
            }),
        [rowHasChanged],
    );

    // Rebuilt whenever the header node changes, which is what makes the diff above see it as
    // changed. Only this one row re-renders — the item rows keep their own identities.
    const headerRow = useMemo<HeaderRow>(
        () => ({ [HEADER_ROW_MARKER]: true, node: header }),
        [header],
    );

    const data = useMemo(
        () => dataProvider.cloneWithRows(hasHeader ? [headerRow, ...items] : [...items]),
        [dataProvider, items, hasHeader, headerRow],
    );

    // A single layout type, so this provider does NOT depend on `hasHeader`. Handing RLV a new
    // LayoutProvider makes it rebuild its LayoutManager from scratch, deliberately WITHOUT the
    // cached layouts (`RecyclerListView._checkAndChangeLayouts`); every mounted row then notices
    // the manager swap and force-emits its measured size 32ms later, which is still {0, 0} for
    // any row whose onLayout has not landed yet, and `LayoutManager.overrideLayout` latches that
    // zero for good. The list becomes a stack of zero-height rows — a second way to the blank
    // screen of BAS-2861, and one that fired here as soon as `hasHeader` flipped when the async
    // email-verification headband showed up on an already-mounted list.
    // Splitting header/row into two types only ever bought separate recycling pools: both types
    // returned the exact same estimated dimensions, and the real ones are measured anyway
    // (`forceNonDeterministicRendering`). The header row is identified from the data instead.
    const layoutProvider = useMemo(
        () =>
            new LayoutProvider(
                () => ROW_LAYOUT_TYPE,
                (_type, dim) => {
                    dim.width = width;
                    dim.height = ESTIMATED_ITEM_HEIGHT;
                },
            ),
        [width],
    );

    // The default WrapGridLayoutManager packs items left-to-right and wraps. With
    // `forceNonDeterministicRendering` each row is measured at its natural content width,
    // which on wide (web/large) layouts is narrower than the container — yielding several
    // items per line. Pinning each row to the measured container width forces one column.
    const rowRenderer = useCallback<RecyclerListViewProps["rowRenderer"]>(
        (_type, item, index) => (
            <View style={{ width }}>
                {isHeaderRow(item)
                    ? item.node
                    : renderItem(item as T, hasHeader ? index - 1 : index)}
            </View>
        ),
        [renderItem, width, hasHeader],
    );

    const handleScroll = useCallback<NonNullable<RecyclerListViewProps["onScroll"]>>(
        (rawEvent, _offsetX, offsetY) => {
            const { layoutMeasurement, contentSize } = rawEvent.nativeEvent;
            const isAtBottom =
                layoutMeasurement != null && contentSize != null
                    ? offsetY + layoutMeasurement.height >= contentSize.height - 10
                    : false;
            onScroll?.(offsetY, isAtBottom);
        },
        [onScroll],
    );

    const renderFooter = useCallback(() => {
        if (!footerContent && !footerHeight) {
            return null;
        }
        return (
            <View style={{ width: "100%" }}>
                {footerContent}
                {footerHeight ? <View style={{ height: footerHeight }} /> : null}
            </View>
        );
    }, [footerContent, footerHeight]);

    const scrollViewProps = useMemo(
        () => (refreshControl ? { refreshControl } : undefined),
        [refreshControl],
    );

    return (
        <View style={[styles.container, style]} onLayout={onContainerLayout}>
            {width > 0 ? (
                <View style={styles.list}>
                    <RecyclerListView
                        style={styles.list}
                        layoutProvider={layoutProvider}
                        dataProvider={data}
                        rowRenderer={rowRenderer}
                        forceNonDeterministicRendering
                        onItemLayout={handleItemLayout}
                        onScroll={handleScroll}
                        onEndReached={onEndReached}
                        onEndReachedThreshold={onEndReachedThreshold}
                        renderFooter={renderFooter}
                        scrollViewProps={scrollViewProps}
                        suppressBoundedSizeException
                    />
                </View>
            ) : null}
            {width > 0 && isCoverMounted ? (
                <Animated.View
                    style={[
                        StyleSheet.absoluteFill,
                        { backgroundColor: colors.background },
                        coverStyle,
                    ]}
                    pointerEvents="none"
                >
                    <Loading style={StyleSheet.absoluteFill} />
                </Animated.View>
            ) : null}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: "100%",
    },
    list: {
        flex: 1,
        minHeight: 1,
    },
});

export default VirtualizedScreenBody;
